<template>
  <BaseModal overlay-id="modalLevels">
    <div class="banner lvl_1">
      <BaseImage :src="levelImage" image-class="lvl_up_img" alt="Level Up" />
    </div>

    <div class="content">
      <h1>{{ $_translations.value.modal_level_completed_title }} {{ level }}</h1>

      <h2>{{ $_translations.value[levelName] }}</h2>

      <template v-if="isFirstLevel">
        <p>{{ $_translations.value.modal_level_completed_first_level_description_pt1 }}</p>

        <p>
          {{ $_translations.value.modal_level_completed_first_level_description_pt2 }}
          <span class="yellow_bold">{{ prizeValue }}</span>
          {{ $_translations.value.modal_level_completed_first_level_description_pt3 }}
        </p>
      </template>

      <template v-else>
        <p>
          {{ $_translations.value.modal_level_completed_rest_levels_description_pt1 }}
          <span class="yellow_bold">{{ levelThreshold }}</span>
          {{ $_translations.value.modal_level_completed_rest_levels_description_pt2 }}
        </p>

        <p>
          {{ $_translations.value.modal_level_completed_rest_levels_description_pt3 }}
          <span class="yellow_bold">{{ prizeValue }}</span>
          {{ $_translations.value.modal_level_completed_rest_levels_description_pt4 }}
        </p>
      </template>

      <RewardBoxItem :prize-value="prizeValue" />

      <BaseButton button-class="fancy_btn close_modal">
        {{ $_translations.value.modal_level_completed_button_proceed }}
      </BaseButton>

      <BaseButton
        container-class="pause-animation"
        @click:button="$emit('redirect:levelsPage')"
      >
        {{ $_translations.value.modal_level_completed_button_show_all_levels }}
      </BaseButton>
    </div>
  </BaseModal>
</template>

<script setup>
import { computed, onMounted, onUnmounted } from 'vue';
import { Howl } from 'howler';
import BaseModal from '@/components/modal/BaseModal.vue';
import BaseImage from '@/components/image/BaseImage.vue';
import BaseButton from '@/components/button/BaseButton.vue';
import RewardBoxItem from '@/components/item/RewardBoxItem.vue';
import useAppSettingsStore from '@/store/modules/appSettings.js';
import useLevelStore from '@/store/modules/level.js';
import useConfettiHook from '@/hooks/useConfetti.js';
import { getLevelNameTranslationKey, getLevelCompletedImage } from '@/helpers/reward.js';

defineEmits(['redirect:levelsPage']);

const props = defineProps({
  data: {
    type: Object,
    default: null,
  },
  playSound: {
    type: Boolean,
    default: true,
  },
});

const appSettingsStore = useAppSettingsStore();
const levelStore = useLevelStore();

const { triggerConfetti, clearConfetti } = useConfettiHook();

const level = computed(() => props.data?.__level ?? 0);

const isFirstLevel = computed(() => level.value === 1);

const levelName = computed(() => getLevelNameTranslationKey(level.value));

const levelImage = computed(() => getLevelCompletedImage(level.value));

const levelItemFromList = computed(() => levelStore.levelList.find((item) => item.__level === level.value));

const levelThreshold = computed(() => levelItemFromList.value?.threshold ?? 0);

const prizeValue = computed(() => props.data?.prizeValue ?? levelItemFromList.value?.prizeValue ?? 0);

onMounted(() => {
  triggerConfetti();

  if (appSettingsStore.isSoundEnabled && props.playSound) {
    const modalSound = new Howl({ src: ['/static/sounds/levelup.mp3'] });
    modalSound.play();
  }
});

onUnmounted(clearConfetti);
</script>
